import { Check, Trophy, Clock, Book } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const StatisticsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ tramites: 0, exito: 0, especialidades: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate counters
          animateCounter('tramites', 10000, 2000);
          animateCounter('exito', 95, 2000);
          animateCounter('especialidades', 17, 2000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const animateCounter = (key, target, duration) => {
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
    }, duration / steps);
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-white via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 shadow-md">
            <span className="text-blue-600 font-semibold text-sm">RESULTADOS COMPROBADOS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Cifras que nos{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Respaldan
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Años de experiencia convertidos en resultados tangibles
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-8">
          <StatCard
            number={counters.tramites > 0 ? `${counters.tramites.toLocaleString()}+` : "10,000+"}
            label="Trámites Procesados"
            icon={<Check className='w-12 h-12' />}
            gradient="from-blue-500 to-cyan-500"
            isVisible={isVisible}
            delay="0ms"
          />
          <StatCard
            number={counters.exito > 0 ? `${counters.exito}%` : "95%"}
            label="Tasa de Éxito"
            icon={<Trophy className='w-12 h-12' />}
            gradient="from-yellow-500 to-orange-500"
            isVisible={isVisible}
            delay="150ms"
          />
          <StatCard
            number="24/7"
            label="Soporte Continuo"
            icon={<Clock className='w-12 h-12' />}
            gradient="from-purple-500 to-pink-500"
            isVisible={isVisible}
            delay="300ms"
          />
          <StatCard
            number={counters.especialidades > 0 ? counters.especialidades : "17"}
            label="Especialidades"
            icon={<Book className='w-12 h-12' />}
            gradient="from-green-500 to-emerald-500"
            isVisible={isVisible}
            delay="450ms"
          />
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ number, label, icon, gradient, isVisible, delay }) => (
  <div
    className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden ${
      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
    }`}
    style={{ transitionDelay: delay }}
  >
    {/* Gradient background on hover */}
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

    <div className="relative z-10 flex flex-col items-center justify-center text-center">
      {/* Icon with gradient */}
      <div className={`w-16 h-16 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
        <div className="text-white">{icon}</div>
      </div>

      <div className="text-4xl md:text-7xl font-bold bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300">
        {number}
      </div>
      <div className="text-lg text-gray-600 font-medium">{label}</div>
    </div>

    {/* Decorative corner */}
    <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl ${gradient} opacity-0 group-hover:opacity-10 rounded-full blur-2xl transition-opacity duration-500`}></div>
  </div>
);

export default StatisticsSection;